/**
 * Minimal, dependency-free Markdown subset → safe HTML.
 *
 * Supports exactly what the package Description field needs:
 *   **bold**            → <strong>
 *   - item / * item     → <ul><li>
 *   1. item             → <ol><li>
 *   single newline      → <br>
 *   blank line          → new paragraph
 *
 * All input is HTML-escaped first, so the output is XSS-safe to inject via
 * dangerouslySetInnerHTML. We deliberately do NOT support raw HTML, links, or
 * images — add them (and a real sanitizer) only if a use case actually needs them.
 */

const esc = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Escape first, then apply inline **bold**. Order matters — esc neutralizes any
// user-supplied angle brackets before we add our own tags.
const inline = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

const isBullet = (l) => /^\s*[-*]\s+/.test(l);
const isNumbered = (l) => /^\s*\d+\.\s+/.test(l);

function renderBlock(block) {
  const lines = block.split('\n').map((l) => l.replace(/\s+$/, ''));
  const nonEmpty = lines.filter((l) => l.trim() !== '');
  if (nonEmpty.length === 0) return '';

  if (nonEmpty.every(isBullet)) {
    return '<ul>' + nonEmpty.map((l) => `<li>${inline(l.replace(/^\s*[-*]\s+/, ''))}</li>`).join('') + '</ul>';
  }
  if (nonEmpty.every(isNumbered)) {
    return '<ol>' + nonEmpty.map((l) => `<li>${inline(l.replace(/^\s*\d+\.\s+/, ''))}</li>`).join('') + '</ol>';
  }
  // Plain paragraph — single newlines become <br>.
  return '<p>' + nonEmpty.map(inline).join('<br>') + '</p>';
}

export function renderMiniMarkdown(text) {
  if (!text) return '';
  return String(text)
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map(renderBlock)
    .filter(Boolean)
    .join('');
}
